import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { TrashOutlineIcon } from "modules/icons";
import CircularIconButton from "../_shared/buttons/circular-icon-button";
import {
  DeleteFileMutation,
  DeleteFileMutationVariables,
  FileGalleryFragment,
} from "gql/graphql";

import {
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import DELETE_FILE_MUTATION from "modules/providers/file-provider/networking/delete-file";
import { useAuth } from "modules/providers/auth-provider";
import {
  FILE_GALLERY_BUTTON_BOX_SIZE,
  FILE_GALLERY_BUTTON_BACKGROUND_COLOR,
  FILE_GALLERY_BUTTON_WIDTH,
} from "modules/utils/Constants";
import { useToggle } from "react-use";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  file: FileGalleryFragment;
  allowDelete: boolean;
}

const FileGalleryDeleteFileButton = ({ file, allowDelete }: IProps) => {
  const { me } = useAuth();
  const { closeModal } = useOverlay();
  const [isOpen, toggleIsOpen] = useToggle(false);

  const [deleteFileAPI] = useMutation<
    DeleteFileMutation,
    DeleteFileMutationVariables
  >(DELETE_FILE_MUTATION);

  const onDeleteFileClicked = useCallback(() => {
    deleteFileAPI({
      variables: {
        id: file.id,
      },
    });

    closeModal();
  }, [closeModal, deleteFileAPI, file]);

  if (file.userId !== me?.id || !allowDelete) {
    return null;
  }

  return (
    <VStack spacing={1} w={FILE_GALLERY_BUTTON_WIDTH}>
      <Popover
        trigger="click"
        isOpen={isOpen}
        onOpen={toggleIsOpen}
        onClose={toggleIsOpen}
      >
        <PopoverTrigger>
          <CircularIconButton
            icon={
              <TrashOutlineIcon
                fill={colors.white}
                boxSize={FILE_GALLERY_BUTTON_BOX_SIZE}
              />
            }
            aria-label="delete"
            backgroundColor={FILE_GALLERY_BUTTON_BACKGROUND_COLOR}
            variant="hover"
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="full">
            <PopoverArrow />
            <PopoverBody>
              <HStack>
                <Text>Delete file?</Text>
                <Button variant="outline" onClick={toggleIsOpen}>
                  Cancel
                </Button>
                <Button onClick={onDeleteFileClicked}>Delete</Button>
              </HStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
      <Text color={colors.grayPrimary} fontWeight={500} fontSize={10}>
        Delete
      </Text>
    </VStack>
  );
};

export default memo(FileGalleryDeleteFileButton);
